import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { FiCalendar, FiUser, FiClock, FiCheckCircle, FiXCircle, FiSearch, FiX } from 'react-icons/fi';
import './IncomingAppointments.css';

export default function IncomingAppointments() {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reschedulingId, setReschedulingId] = useState(null);
    const [newDateTime, setNewDateTime] = useState('');
    const { userRole, currentCoordinator } = useAuth();
    const [confirmAction, setConfirmAction] = useState(null);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [filters, setFilters] = useState({ search: '', status: '' });

    useEffect(() => {
        let q;
        if (userRole === 'coordinator') {
            q = query(
                collection(db, 'appointments'), 
                orderBy('createdAt', 'desc')
            );
        } else {
            q = query(
                collection(db, 'appointments'), 
                orderBy('createdAt', 'desc')
            );
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            let apps = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Only show appointments that have a doctorId (Guild appointments)
            apps = apps.filter(a => a.doctorId);

            setAppointments(apps);
            setFilteredAppointments(apps);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching appointments:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userRole, currentCoordinator]);

    useEffect(() => {
        let filtered = [...appointments];
        
        if (filters.status) {
            filtered = filtered.filter(a => (a.status || 'pending') === filters.status);
        }
        
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(a => 
                (a.patientName?.toLowerCase().includes(searchLower)) ||
                (a.patientPhone?.toLowerCase().includes(searchLower)) ||
                (a.doctorName?.toLowerCase().includes(searchLower))
            );
        }
        
        setFilteredAppointments(filtered);
    }, [appointments, filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ search: '', status: '' });
    };

    const handleActionClick = (id, actionType) => {
        if (actionType === 'rescheduled') {
            setNewDateTime('');
        }
        setConfirmAction({ id, actionType });
    };

    const handleConfirmSubmit = async () => {
        if (!confirmAction) return;
        
        const { id, actionType } = confirmAction;

        if (actionType === 'rescheduled') {
            if (!newDateTime) {
                alert('Please select a valid date and time.');
                return;
            }
            try {
                const dateObj = new Date(newDateTime);
                await updateDoc(doc(db, 'appointments', id), {
                    status: 'rescheduled',
                    scheduledAt: Timestamp.fromDate(dateObj)
                });
                setConfirmAction(null);
                setNewDateTime('');
            } catch (error) {
                console.error('Error rescheduling:', error);
                alert('Failed to reschedule');
            }
        } else {
            try {
                await updateDoc(doc(db, 'appointments', id), {
                    status: actionType
                });
                setConfirmAction(null);
            } catch (error) {
                console.error('Error updating status:', error);
                alert('Failed to update status');
            }
        }
    };

    if (loading) return <div className="appointments-container text-center">Loading appointments...</div>;

    return (
        <div className="appointments-container">
            <div className="appointments-header">
                <h1>Incoming Appointments</h1>
                <p>Manage patient appointments assigned to Guild Doctors.</p>
            </div>

            <div className="filters-section">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        name="search"
                        placeholder="Search by patient name, phone, or doctor..."
                        value={filters.search}
                        onChange={handleFilterChange}
                    />
                </div>

                <div className="filter-group">
                    <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="rescheduled">Rescheduled</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>

                {(filters.search || filters.status) && (
                    <button onClick={clearFilters} className="btn-clear-filters">
                        <FiX />
                        <span>Clear Filters</span>
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Patient</th>
                            <th>Doctor</th>
                            <th>Schedule</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAppointments.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="empty-state">
                                    <div className="empty-content">
                                        <FiCalendar className="empty-icon" />
                                        <h3>No Appointments</h3>
                                        <p>There are no incoming appointments at this time.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredAppointments.map(app => {
                                let dateStr = 'TBD';
                                let timeStr = 'TBD';
                                if (app.scheduledAt) {
                                    const d = app.scheduledAt.toDate();
                                    dateStr = d.toLocaleDateString();
                                    timeStr = d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                                } else if (app.appointmentDate) {
                                    dateStr = app.appointmentDate.toDate ? app.appointmentDate.toDate().toLocaleDateString() : app.appointmentDate;
                                    timeStr = app.appointmentTime || 'TBD';
                                }

                                return (
                                    <tr key={app.id} className="clickable-row">
                                        <td className="name-cell">
                                            <strong>{app.patientName || 'Unknown'}</strong>
                                            <span className="sub-text">{app.patientPhone || 'No Phone'}</span>
                                            {app.reason && <span className="sub-text" style={{ fontStyle: 'italic', marginTop: '4px' }}>Note: {app.reason}</span>}
                                        </td>
                                        <td className="name-cell">
                                            <strong>Dr. {app.doctorName || 'Unknown'}</strong>
                                            <span className="sub-text">{app.specialty || 'General'}</span>
                                        </td>
                                        <td className="name-cell">
                                            <strong>{dateStr}</strong>
                                            <span className="sub-text">{timeStr}</span>
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${app.status || 'pending'}`}>
                                                {app.status || 'Pending'}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            <div className="action-buttons">
                                                {app.status === 'pending' && (
                                                    <>
                                                        <button onClick={() => handleActionClick(app.id, 'confirmed')} className="btn-icon btn-confirm-action" title="Confirm">
                                                            <FiCheckCircle size={20} />
                                                        </button>
                                                        <button onClick={() => handleActionClick(app.id, 'rescheduled')} className="btn-icon btn-reschedule-action" title="Reschedule">
                                                            <FiCalendar size={20} />
                                                        </button>
                                                        <button onClick={() => handleActionClick(app.id, 'cancelled')} className="btn-icon btn-cancel-action" title="Cancel">
                                                            <FiXCircle size={20} />
                                                        </button>
                                                    </>
                                                )}
                                                {(app.status === 'confirmed' || app.status === 'rescheduled') && (
                                                    <>
                                                        <button onClick={() => handleActionClick(app.id, 'completed')} className="btn-icon btn-complete-action" title="Mark Completed">
                                                            <FiCheckCircle size={20} />
                                                        </button>
                                                        <button onClick={() => handleActionClick(app.id, 'rescheduled')} className="btn-icon btn-reschedule-action" title="Reschedule">
                                                            <FiCalendar size={20} />
                                                        </button>
                                                        <button onClick={() => handleActionClick(app.id, 'cancelled')} className="btn-icon btn-cancel-action" title="Cancel">
                                                            <FiXCircle size={20} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {confirmAction && (
                <div className="confirmation-modal-overlay" onClick={() => setConfirmAction(null)}>
                    <div className="confirmation-modal" onClick={e => e.stopPropagation()}>
                        <h3>Confirm Action</h3>
                        <p>
                            Are you sure you want to mark this appointment as 
                            <strong> {confirmAction.actionType.toUpperCase()}</strong>?
                        </p>
                        
                        {confirmAction.actionType === 'rescheduled' && (
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>Select New Date & Time:</label>
                                <input 
                                    type="datetime-local" 
                                    value={newDateTime} 
                                    onChange={(e) => setNewDateTime(e.target.value)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                />
                            </div>
                        )}

                        <div className="modal-actions">
                            <button onClick={() => setConfirmAction(null)} className="modal-btn modal-btn-cancel">
                                Cancel
                            </button>
                            <button onClick={handleConfirmSubmit} className="modal-btn modal-btn-confirm">
                                Yes, Proceed
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
